/**
 * @fileoverview "Pause until spring instead?": what a monthly subscriber is offered when
 * they go to cancel, and what the price notice email's "Pause instead"
 * link opens (/subscribe?pause=1).
 *
 * The days to choose from are the gardener's own renewal days, up to the
 * longest pause (the Grow setting subscription.PAUSE_MAX_MONTHS), so
 * "Billing starts again on February 17" is exactly when the next charge
 * is; the one nearest March 1 is chosen first. "Cancel anyway" opens
 * Stripe's portal on its cancel step. A plan that cannot be paused (yearly,
 * cancelling, or before the pause SQL has run) is not offered one: going to
 * cancel then opens the portal's cancel step directly, and the email's link
 * just closes it.
 *
 * Where: a dialog over the subscribe page (SubscribePage), opened by "Cancel
 * subscription" (cancelling) or by ?pause=1.
 * API: GET /billing/pause (getPauseOffer) each time it opens; POST
 * /billing/pause { resumeAt } (pausePlan); POST /billing/portal
 * { flow: 'cancel' } (openPortal, "Cancel anyway").
 * Settings: subscription.PAUSE_MAX_MONTHS (the server's options).
 * Foot: ActionRow (B111): Cancel anyway quiet, far left beside the pair when they fit, on its own line above it when they do not; Keep my plan (Cancel) and Pause in the pair. The button whose errand waits shows
 * Button's spinner and word (Pause: Saving..., Cancel anyway: Opening...). The "One moment" row keeps its Spinner (it is not a button).
 * Words (en/es): billing.pause.*, sitewide.opening. A failure on our side (a 500, no
 * connection; D31, lib/serverFailure) says the standard sentence, reported
 * to Sentry, with the support address as a link, in place of
 * billing.pause.failed's "Try again in a moment" (lib/errorMessage).
 * Watch out: there is no pause-offer email any more (removed 2026-10-07);
 * the only email that links here is the price notice, on a monthly plan.
 *
 * @module PauseOffer
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import Spinner from '@/shared/components/ui/Spinner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { getPauseOffer, openPortal, pausePlan, type PauseOffer as Offer } from '@/shared/api/billing';
import { BILLING_QUERY_KEY } from '../lib/useBilling';
import { pauseDay } from '../lib/pauseDay';
import { usePageRestored } from '@/shared/hooks/usePageRestored';

/** What the dialog is doing. */
type Step = 'loading' | 'offer' | 'working' | 'failed';

/** Which errand a 'working' step is: pausing the plan, or leaving for Stripe's cancel step (only that button shows it waits). */
type Errand = 'pause' | 'portal';

/**
 * The pause offer, opened over the subscribe page.
 *
 * @param props.open - Whether it is open.
 * @param props.cancelling - Opened by going to cancel (not by the email's link): with no pause to offer, cancelling goes on.
 * @param props.onClose - Closes it (kept the plan, or paused).
 * @param props.onPaused - Told the day billing starts again once paused, to say so on the page.
 */
export const PauseOffer: React.FC<{
  open: boolean;
  cancelling: boolean;
  onClose: () => void;
  onPaused: (until: string) => void;
}> = ({ open, cancelling, onClose, onPaused }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = React.useState<Step>('loading');
  const [offer, setOffer] = React.useState<Offer | null>(null);
  const [chosen, setChosen] = React.useState<string>('');
  // What the last failure threw, for its words (lib/errorMessage).
  const [failure, setFailure] = React.useState<unknown>(null);
  // Which button's errand is running, so only that button shows its spinner and word.
  const [errand, setErrand] = React.useState<Errand>('pause');
  // The latest onClose, so a parent's new function each render does not fetch the days again.
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  // Back from Stripe's portal to this page as it was left, the dialog still
  // working and impossible to close: it closes.
  usePageRestored(() => {
    if (step === 'working') closeRef.current();
  });

  /** Leaves for Stripe's portal on its cancel step; says so here if it cannot open. */
  const cancelAnyway = React.useCallback(async (): Promise<void> => {
    setErrand('portal');
    setStep('working');
    try {
      window.location.assign(await openPortal('cancel'));
    } catch (error) {
      setFailure(error);
      setStep('failed');
    }
  }, []);

  // Each time it opens: the days as they are now. No pause to offer: straight
  // to cancelling when that is where they were going, else nothing to show.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStep('loading');
    getPauseOffer()
      .then((result) => {
        if (cancelled) return;
        if (!result.available || result.options.length === 0) {
          if (cancelling) void cancelAnyway();
          else closeRef.current();
          return;
        }
        setOffer(result);
        setChosen(result.suggested ?? result.options[result.options.length - 1]);
        setStep('offer');
      })
      .catch((error) => { if (!cancelled) { setFailure(error); setStep('failed'); } });
    return () => { cancelled = true; };
  }, [open, cancelling, cancelAnyway]);

  /** Pauses until the chosen day, then refreshes where the gardener stands. */
  const pause = async (): Promise<void> => {
    if (!chosen) return;
    setErrand('pause');
    setStep('working');
    try {
      await pausePlan(chosen);
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
      onPaused(chosen);
      onClose();
    } catch (error) {
      setFailure(error);
      setStep('failed');
    }
  };

  const busy = step === 'loading' || step === 'working';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('billing.pause.title')}</DialogTitle>
          <DialogDescription>{t('billing.pause.body')}</DialogDescription>
        </DialogHeader>

        {step === 'loading' && (
          <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground"><Spinner size={18} />{t('billing.pause.loading')}</div>
        )}

        {offer && step !== 'loading' && (
          <div className="space-y-3">
            <label className="flex flex-wrap items-center gap-2 text-sm">
              <span>{t('billing.pause.billingAgain')}</span>
              <Select value={chosen} onValueChange={setChosen} disabled={busy}>
                <SelectTrigger className="h-9 w-auto min-w-[9rem]" aria-label={t('billing.pause.billingAgain')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {offer.options.map((option) => (
                    <SelectItem key={option} value={option}>{pauseDay(option, language)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <p className="text-xs text-muted-foreground">{t('billing.pause.note', { from: pauseDay(offer.pausedFrom, language) })}</p>
          </div>
        )}

        {step === 'failed' && <p role="alert" className="text-sm text-destructive"><FailureText text={errorMessage(failure, t('billing.pause.failed'))} /></p>}

        <DialogFooter>
          <ActionRow
            onCancel={onClose}
            cancelLabel={t('billing.pause.keep')}
            disabled={busy}
            onRemove={() => { void cancelAnyway(); }}
            removeLabel={t('billing.pause.cancelAnyway')}
            removeLoading={step === 'working' && errand === 'portal'}
            removeBusy="opening"
          >
            <Button loading={step === 'working' && errand === 'pause'} disabled={busy || !offer || !chosen} onClick={() => { void pause(); }}>
              {t('billing.pause.pauseButton', { date: pauseDay(chosen, language) })}
            </Button>
          </ActionRow>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PauseOffer;
